import * as React from 'react';
import BPromise from 'bluebird';
import Lazy from 'lazy.js';
import { AppUser } from 'Src/views/App/App';
import { parse, stringify } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import {
  getEmailOrMisEmail,
  getPhoneOrMisPhone,
  getUserForms,
  getUserHouses,
  getUserRole,
  getUserStatus,
  getUserTags
} from 'Src/helpers/accessor/accessor';
import { SchoolUser } from 'Src/models/schoolUser';
import { getSchoolForms, getSchoolHouses, getSchoolSports } from 'Src/helpers/service/admin/user';
import { getSelectOptionForTag, getSelectOptionForUserStatus } from 'Src/helpers/table/select';
import {
  deleteDataToPermission,
  getAllSchoolStaffs,
  searchSchoolStaffs,
  postUserPermission,
  putDataToPermission,
  putUserPermission,
  updatePermissionsInBulk
} from 'Src/helpers/service/admin/staffs';
import { createUserTag, getAllUserTags } from 'Src/helpers/service/admin/userTags';
import { GeneralMessageWizzard } from 'Src/components/GeneralMessageWizzard/GeneralMessageWizzard';
import { RoleForm } from 'Src/components/RoleForm/RoleForm';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { TextUtils } from 'Src/helpers/utils/TextUtils';
import { HouseRemoveModal } from 'Src/components/StaffListModals/HouseRemoveModal';
import { FormRemoveModal } from 'Src/components/StaffListModals/FormRemoveModal';
import { HouseAssignModal } from 'Src/components/StaffListModals/HouseAssignModal';
import { FormAssignModal } from 'Src/components/StaffListModals/FormAssignModal';
import propz from 'propz';
import { ROLE, ROLE_SERVER_TO_CLIENT_MAPPING, STAFF_LIST } from 'Src/consts/user';
import { isSchoolRoleEnabled, getCreatableStaffRoles } from 'Src/helpers/permission/schoolRoles';
import { CreateStaffModal } from 'Src/components/StaffListModals/CreateStaffModal';
import { staffMisOperations } from 'Src/helpers/permission/staffMis';
import { MEMBER_OF_STAFF_TABS } from 'Src/consts/school';
import { StaffSummary } from 'Src/components/StaffSummary/StaffSummary';
import { getSelectOptionForForms, getSelectOptionForHouses, getSelectOptionForStaffs } from 'Src/helpers/table/select';
import { getUserTagColor } from 'Src/helpers/tags/tagColors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTags, faUserShield, faUserTie } from '@fortawesome/free-solid-svg-icons';
import '../Students/Students.scss';
import { countActivePeopleFilters, PeopleFilterChips } from '../PeopleFilterChips';
import { PeopleFilterChipSelect } from '../PeopleFilterChipSelect';
import { SearchMultiSelectFilter } from 'Src/components/SearchMultiSelectFilter/SearchMultiSelectFilter';
import './Staffs.css';
import { RoleRemoveModal } from 'Src/components/StaffListModals/RoleRemoveModal';
import { getSettingsUploadFiles } from 'Src/helpers/service/nobody/settings';
import { SchoolForm } from '../../../../../models/form';
import { SchoolHouse } from '../../../../../models/house';
import { Sport } from '../../../../../models/sport';
import { Tag } from 'Src/models/tag';
import { UserTagAssignModal } from 'Src/components/UserTagAssignModal/UserTagAssignModal';
import { UserTagDeleteModal } from 'Src/components/UserTagDeleteModal/UserTagDeleteModal';
import { updateUserTagsInBulk } from 'Src/helpers/service/admin/users';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { PeopleListPageHeader } from '../PeopleListPageHeader';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';

function normalizeStaffRoleName(role: string): string {
  return (
    ROLE_SERVER_TO_CLIENT_MAPPING[role] ||
    String(role || '')
      .toLowerCase()
      .replace(/^./, char => char.toUpperCase())
  );
}

function getStaffRoleNames(user: SchoolUser): string[] {
  const roles = Lazy(user.permissions)
    .map(permission => permission.preset)
    .uniq()
    .toArray() as string[];
  return user.permissions.some(permission => permission.isExcludedFromMisIntegration === true)
    ? [...roles, 'External staff']
    : roles;
}

function getStaffSportNames(user: SchoolUser, sports: Sport[] = []): string[] {
  const sportsById = new Map(sports.map(sport => [sport.id, sport.name]));

  return Lazy(user.permissions)
    .map(permission => {
      const expandedSports = propz.get(permission, ['sports'], []);

      if (expandedSports.length > 0) {
        return expandedSports.map((sport: any) => sport.name || sport.title || sport.id || '');
      }

      return propz.get(permission, ['sportIds'], []).map((sportId: string) => sportsById.get(sportId) || sportId);
    })
    .flatten()
    .filter(name => name !== '')
    .uniq()
    .toArray() as string[];
}

function renderStaffChips(values: string[], type: 'role' | 'sport'): JSX.Element {
  const visibleValues = values.slice(0, 2);
  const moreCount = Math.max(values.length - visibleValues.length, 0);

  return (
    <td>
      <div className="staff-chipCell">
        {visibleValues.length > 0 ? (
          visibleValues.map(value => (
            <span key={`${type}_${value}`} className={`staff-chip staff-chip--${type}`}>
              {type === 'role' ? normalizeStaffRoleName(value) : value}
            </span>
          ))
        ) : (
          <span className="staff-chipEmpty">-</span>
        )}
        {moreCount > 0 && <span className="staff-chipMore">+{moreCount} more</span>}
      </div>
    </td>
  );
}
const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['firstName']
  },
  {
    text: 'Surname',
    field: 'lastName',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['lastName']
  },
  {
    text: 'Email',
    field: 'email',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getEmailOrMisEmail
  },
  {
    text: 'Phone',
    field: 'phone',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: getPhoneOrMisPhone
  },
  {
    text: 'Role',
    field: 'role',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    cell: item => renderStaffChips(getStaffRoleNames(item), 'role')
  },
  {
    text: 'Status',
    field: 'userStatus',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserStatus
  },
  {
    text: 'Sports',
    field: 'sports',
    isSort: false,
    cell: item => renderStaffChips(getStaffSportNames(item), 'sport')
  },
  {
    text: 'Forms',
    field: 'permissionFormIds',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserForms
  },
  {
    text: 'Houses',
    field: 'permissionHouseIds',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserHouses
  },
  {
    text: 'Tag',
    field: 'tag',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getUserTags
  }
];

const NAME_SEARCH_COLUMN: ColumnDefinition = {
  text: 'Name',
  field: 'nameSearch',
  isSort: false,
  type: FILTER_TYPE.TEXT,
  accessor: ['firstName']
};

const FILTER_COLUMNS: ColumnDefinition[] = [...COLUMNS, NAME_SEARCH_COLUMN];

interface State {
  items: SchoolUser[];
  currentPage: number;
  selectedItems: SchoolUser[];
  previewStaff: SchoolUser | null;
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  excludedSelectionIds: string[];
  total: number;
  basePath: string;
  isMessageModalOpen: boolean;
  isUserTagModalOpen: boolean;
  isUserTagDeleteModalOpen: boolean;
  currentTabIndex: number;
  editablePermissionId: string;
  isAddRoleModalOpen: boolean;
  isCreateStaffModalOpen: boolean;
  isListActionsModalOpen: boolean;
  activeListAction: STAFF_LIST_ACTIONS;
  maxSizeUploadFile: number;
  forms: SchoolForm[];
  houses: SchoolHouse[];
  sports: Sport[];
  tags: Tag[];
  selectedUserTagId: string;
  hiddenStaffColumnKeys: string[];
  isColumnsMenuOpen: boolean;
  isSelectionMenuOpen: boolean;
  inlineRevokePermission: any | null;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

enum STAFF_LIST_ACTIONS {
  ASSIGN_FORM = 'ASSIGN_FORM',
  ASSIGN_HOUSE = 'ASSIGN_HOUSE',
  REMOVE_FORM = 'REMOVE_FORM',
  REMOVE_HOUSE = 'REMOVE_HOUSE',
  REMOVE_ROLE = 'REMOVE_ROLE'
}

interface InlineMultiAddProps {
  label: string;
  options: Array<{ id: string; name: string }>;
  onApply: (ids: string[]) => void;
}

const InlineMultiAdd: React.FunctionComponent<InlineMultiAddProps> = ({ label, options, onApply }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const closeOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [isOpen]);

  const visibleOptions = options
    .filter(option => option.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
  const close = () => {
    setIsOpen(false);
    setSearch('');
    setSelectedIds([]);
  };

  return (
    <div className="staff-inlineMulti" ref={rootRef}>
      <button type="button" className="staff-accessAddButton" onClick={() => setIsOpen(open => !open)}>
        {label}
      </button>
      {isOpen ? (
        <div className="staff-inlineMulti__popover">
          <input autoFocus value={search} placeholder="Search…" onChange={event => setSearch(event.target.value)} />
          <div className="staff-inlineMulti__options">
            {visibleOptions.length ? (
              visibleOptions.map(option => (
                <label key={option.id}>
                  <input
                    type="checkbox"
                    checked={selectedIds.indexOf(option.id) !== -1}
                    onChange={() =>
                      setSelectedIds(ids =>
                        ids.indexOf(option.id) === -1 ? [...ids, option.id] : ids.filter(id => id !== option.id)
                      )
                    }
                  />
                  <span>{option.name}</span>
                </label>
              ))
            ) : (
              <span className="staff-inlineMulti__empty">No matches</span>
            )}
          </div>
          <div className="staff-inlineMulti__actions">
            <button type="button" className="sit-btn sit-btn--ghost" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--primary"
              disabled={!selectedIds.length}
              onClick={() => {
                onApply(selectedIds);
                close();
              }}
            >
              Add {selectedIds.length || ''}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export class Staffs extends React.Component<Props, State> {
  private itemsRequestId = 0;

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      previewStaff: null,
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      total: 0,
      basePath: '',
      isMessageModalOpen: false,
      isUserTagModalOpen: false,
      isUserTagDeleteModalOpen: false,
      currentTabIndex: 0,
      editablePermissionId: '',
      isAddRoleModalOpen: false,
      isCreateStaffModalOpen: false,
      isListActionsModalOpen: false,
      activeListAction: null,
      maxSizeUploadFile: 0,
      forms: [],
      houses: [],
      sports: [],
      tags: [],
      selectedUserTagId: '',
      hiddenStaffColumnKeys: this.getInitialHiddenStaffColumnKeys(),
      isColumnsMenuOpen: false,
      isSelectionMenuOpen: false,
      inlineRevokePermission: null
    };
  }

  getStaffColumnsStorageKey(): string {
    const { user } = this.props;
    const schoolId = propz.get(user, ['activeSchoolId'], propz.get(user, ['activeSchool', 'id'], 'school'));
    const userId = propz.get(user, ['userId'], propz.get(user, ['id'], propz.get(user, ['profile', 'id'], 'user')));
    const role = propz.get(user, ['activeRole'], 'role');

    return `sit.staff.hiddenColumns:${schoolId}:${userId}:${role}`;
  }

  getInitialHiddenStaffColumnKeys(): string[] {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      return JSON.parse(window.localStorage.getItem(this.getStaffColumnsStorageKey()) || '[]');
    } catch (error) {
      return [];
    }
  }

  persistHiddenStaffColumnKeys = (hiddenStaffColumnKeys: string[]): void => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(this.getStaffColumnsStorageKey(), JSON.stringify(hiddenStaffColumnKeys));
    }

    this.setState({ hiddenStaffColumnKeys });
  };

  toggleStaffColumn = (field: string): void => {
    const hidden = new Set(this.state.hiddenStaffColumnKeys);

    if (hidden.has(field)) {
      hidden.delete(field);
    } else {
      hidden.add(field);
    }

    this.persistHiddenStaffColumnKeys(Array.from(hidden));
  };

  resetStaffColumns = (): void => {
    this.persistHiddenStaffColumnKeys([]);
  };

  onStaffPreviewClick = (staff: SchoolUser): void => {
    this.setState({
      previewStaff: staff,
      currentTabIndex: 0
    });
  };

  closeStaffPreview = (): void => {
    this.setState({
      previewStaff: null,
      currentTabIndex: 0
    });
  };

  usePreviewStaffForAction = (callback?: () => void): void => {
    const { previewStaff } = this.state;

    if (!previewStaff) {
      return;
    }

    this.setState(
      {
        selectedItems: [previewStaff],
        isSelectAllChecked: false
      },
      callback
    );
  };

  onStaffRowCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>, staff: SchoolUser): void => {
    event.stopPropagation();

    if (this.state.isSelectAllChecked) {
      const excludedSelectionIds = [...this.state.excludedSelectionIds];
      const excludedIndex = excludedSelectionIds.indexOf(staff.id);
      if (excludedIndex === -1) excludedSelectionIds.push(staff.id);
      else excludedSelectionIds.splice(excludedIndex, 1);
      this.setState({ excludedSelectionIds });
      return;
    }

    const selectedItems = [...this.state.selectedItems];
    const selectedItemIndex = selectedItems.findIndex(item => item.id === staff.id);

    if (selectedItemIndex === -1) {
      selectedItems.push(staff);
    } else {
      selectedItems.splice(selectedItemIndex, 1);
    }

    this.setState({ selectedItems });
  };

  onModernFilterChange = (field: string, value: string): void => {
    const filters = { ...this.state.filters };

    if (value === '') {
      delete filters[field];
    } else {
      const filter = FILTER_COLUMNS.find(col => col.field === field);
      filters[field] = filter && filter.type === FILTER_TYPE.MULTISELECT ? [value] : value;
    }

    this.setState({ filters });
  };

  onModernMultiFilterToggle = (
    field: 'role' | 'userStatus' | 'tag' | 'permissionFormIds' | 'permissionHouseIds',
    value: string
  ): void => {
    const current = Array.isArray(this.state.filters[field])
      ? this.state.filters[field]
      : this.state.filters[field]
      ? [this.state.filters[field]]
      : [];
    this.setState({
      filters: {
        ...this.state.filters,
        [field]: current.indexOf(value) === -1 ? [...current, value] : current.filter(item => item !== value)
      }
    });
  };

  onModernFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onApplyFilterClick();
    }
  };
  onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;

    if (
      target &&
      target.closest &&
      (target.closest('.people-columns') ||
        target.closest('.people-filter') ||
        target.closest('.people-filterBtn') ||
        target.closest('.people-bulkSelect'))
    ) {
      return;
    }

    const shouldRestoreFilters = this.state.isShowFilter;
    this.setState({ isColumnsMenuOpen: false, isShowFilter: false, isSelectionMenuOpen: false });
    if (shouldRestoreFilters) this.setItems();
  };
  onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      const shouldRestoreFilters = this.state.isShowFilter;
      this.setState({ isColumnsMenuOpen: false, isShowFilter: false, isSelectionMenuOpen: false });
      if (shouldRestoreFilters) this.setItems();
    }
  };
  componentDidMount() {
    this.setState({
      isLoading: true
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeyDown);

    this.setAdditionalItems()
      .catch(error => console.error('Unable to load staff list filters', error))
      .then(() => this.setItems());
  }

  componentWillUnmount() {
    this.itemsRequestId += 1;
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setItems();
    }
  }

  setAdditionalItems() {
    const { user } = this.props;

    const staffTags = getAllUserTags(user);
    const formsPromise = getSchoolForms(user, { limit: DEFAULT_LIMIT, skip: DEFAULT_SKIP });
    const housesPromise = getSchoolHouses(user, { limit: DEFAULT_LIMIT, skip: DEFAULT_SKIP });
    const settingsUploadPromise = getSettingsUploadFiles(user);
    const sportsPromise = getSchoolSports(user, {
      limit: DEFAULT_LIMIT,
      skip: DEFAULT_SKIP,
      order: 'name ASC'
    } as any);

    return BPromise.all([staffTags, formsPromise, housesPromise, settingsUploadPromise, sportsPromise]).then(
      ([tags, forms, houses, maxSizeUploadFile, sports]) => {
        this.setState({
          forms,
          houses,
          maxSizeUploadFile,
          tags,
          sports
        });

        return true;
      }
    );
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const parsedPage = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;
    const page = Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(FILTER_COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const requestId = ++this.itemsRequestId;
    this.setState({ isLoading: true });

    return searchSchoolStaffs(user, serverQueryFilter)
      .then(({ items, total }) => {
        if (requestId !== this.itemsRequestId) return false;
        const lastPage = Math.max(FIRST_PAGE, Math.ceil(total / LIMIT));
        if (page > lastPage) {
          const nextSearch = { ...search };
          if (lastPage === FIRST_PAGE) delete nextSearch.page;
          else nextSearch.page = String(lastPage);
          history.replace({ pathname: history.location.pathname, search: stringify(nextSearch) });
          return false;
        }
        this.setState({
          currentPage: page,
          sortDirection: sortDirection,
          sortColumnsName: sortColumnsName,
          isShowFilter: false,
          isDataFiltered: isDataFiltered,
          filters: filters,
          items: items,
          previewStaff: this.state.previewStaff
            ? items.find(item => item.id === this.state.previewStaff.id) || null
            : null,
          total,
          basePath: history.location.pathname,
          isLoading: false
        });

        return true;
      })
      .catch(error => {
        if (requestId === this.itemsRequestId) this.setState({ isLoading: false });
        console.error('Unable to load staff', error);
        return false;
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const nextSelectedItems = [...selectedItems];

    items.forEach(item => {
      if (!selectedIds.has(item.id)) {
        nextSelectedItems.push(item);
      }
    });

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      isSelectionMenuOpen: false,
      inlineRevokePermission: null
    });
  };

  onSelectAllClick = (): void => {
    this.setState({
      isSelectAllChecked: true,
      selectedItems: [],
      excludedSelectionIds: [],
      isSelectionMenuOpen: false
    }, () => this.resolveSelectedStaffForBulkAction(() => undefined));
  };

  resolveSelectedStaffForBulkAction = (callback: () => void): void => {
    const { isSelectAllChecked, excludedSelectionIds, filters } = this.state;

    if (!isSelectAllChecked) {
      callback();
      return;
    }

    this.setState({ isLoading: true });

    const user = this.props.user;
    const where = getServerFieldSectionWhere(filters);

    getAllSchoolStaffs(user, where)
      .then(items => {
        const excludedIds = new Set(excludedSelectionIds);
        const nextSelectedItems = items.filter(item => !excludedIds.has(item.id));

        this.setState(
          {
            selectedItems: nextSelectedItems,
            isLoading: false
          },
          callback
        );
      })
      .catch(error => {
        this.setState({ isLoading: false });
        console.error('Unable to resolve all selected staff', error);
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      isSelectionMenuOpen: false
    });
  };
  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = FILTER_COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      filters: {},
      selectedItems: [],
      isSelectAllChecked: false,
      excludedSelectionIds: [],
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter,
      isColumnsMenuOpen: false
    });
  };

  renderGMWizzard(): React.ReactNode {
    const { user } = this.props;
    const { selectedItems, isMessageModalOpen, maxSizeUploadFile } = this.state;

    if (!isMessageModalOpen) {
      return null;
    }

    return (
      <GeneralMessageWizzard
        user={user}
        onMessageSubmit={this.onMessageSubmitClick}
        onCancelClick={this.onCloseMessageWizardClick}
        schoolStaff={selectedItems}
        sendToStaff={true}
        isOpen={isMessageModalOpen}
        maxSizeUploadFile={maxSizeUploadFile}
      />
    );
  }

  onCloseMessageWizardClick = () => {
    this.setState({
      isMessageModalOpen: false
    });
  };

  onMessageSubmitClick = message => {
    const { history } = this.props;

    history.push({
      pathname: '/generalMessages',
      state: { message: message }
    });
  };

  renderAddRoleModal(): React.ReactNode {
    const { editablePermissionId, isAddRoleModalOpen, sports, previewStaff, selectedItems } = this.state;
    const selectedItem = previewStaff || selectedItems[0];

    if (typeof selectedItem === 'undefined') {
      return null;
    }

    const permission = selectedItem.permissions.find(permission => permission.id === editablePermissionId);

    return (
      <SimpleModal
        customClass="staffRoleModal"
        isOpen={isAddRoleModalOpen}
        title={permission ? 'Edit role' : 'Add role'}
        showFooter={false}
        isBodyBold={false}
        onCloseClick={this.onAddRoleCancelClick}
      >
        <RoleForm
          user={this.props.user}
          onSubmit={this.onRoleSubmitClick}
          onCancel={this.onAddRoleCancelClick}
          permission={permission}
          selectedItem={selectedItem}
          sports={sports}
        />
      </SimpleModal>
    );
  }

  onAddRoleCancelClick = (): void => {
    this.setState({
      isAddRoleModalOpen: false
    });
  };

  onRoleSubmitClick = data => {
    let promise;

    this.setState({
      isLoading: true
    });

    const selectedItem = this.state.previewStaff || this.state.selectedItems[0];

    if (!selectedItem) {
      this.setState({ isLoading: false });
      return;
    }

    if (data.preset !== ROLE.COACH && data.preset !== ROLE.TEACHER) {
      delete data.sportIds;
      delete data.sports;
    }

    if (TextUtils.isEmpty(data.id)) {
      promise = postUserPermission(this.props.user, selectedItem.id, data);
    } else {
      promise = putUserPermission(this.props.user, selectedItem.id, data);
    }

    promise.then(item => {
      if (TextUtils.isEmpty(data.id)) {
        selectedItem.permissions.push(item);
      } else {
        const prevPermissionIndex = selectedItem.permissions.findIndex(permission => permission.id === item.id);
        selectedItem.permissions.splice(prevPermissionIndex, 1, item);
      }

      this.setState({
        isAddRoleModalOpen: false,
        editablePermissionId: '',
        selectedItems: [selectedItem],
        previewStaff: selectedItem
      });
      this.setItems();
    });
  };

  getSelectedUsersIdWithStaffPermission(): { userId: string; permission: any }[] {
    const { selectedItems } = this.state;
    let idsList: { userId: string; permission: any }[] = [];
    const isManager = this.props.user.activeRole === ROLE.MANAGER;

    [...selectedItems].forEach(staff => {
      const permission = staff.permissions.find(
        perm =>
          STAFF_LIST.findIndex(preset => preset === perm.preset) !== -1 &&
          (!isManager || (perm.preset !== ROLE.ADMIN && perm.preset !== ROLE.MANAGER && perm.preset !== ROLE.SLT))
      );

      if (permission) {
        idsList.push({ userId: staff.id, permission: permission });
      }
    });

    return idsList;
  }

  onAssignFormsClick = (forms): void => {
    const { user } = this.props;
    const formIds = forms.map(form => form.id);
    const userAndPermissionIdList = this.getSelectedUsersIdWithStaffPermission();
    const operations = userAndPermissionIdList.map(({ userId, permission }) => ({
      userId,
      permissionId: permission.id,
      data: { formIds: Array.from(new Set([...propz.get(permission, ['formIds'], []), ...formIds])) as string[] }
    }));

    this.setState({
      isLoading: true
    });

    updatePermissionsInBulk(user, 'UPDATE', operations)
      .then(() => {
        this.setState({ selectedItems: [], isSelectAllChecked: false, excludedSelectionIds: [] });
        this.onCloseActionsPopupClick();
        this.setItems();
      })
      .catch(error => {
        console.error('Can not assign forms to staff', error);
        this.setState({ isLoading: false });
      });
  };

  onCloseActionsPopupClick = (): void => {
    this.setState({
      isListActionsModalOpen: false,
      activeListAction: null
    });
  };

  onAssignHouseClick = (houses): void => {
    const { user } = this.props;
    const houseIds = houses.map(house => house.id);
    const userAndPermissionIdList = this.getSelectedUsersIdWithStaffPermission();
    const operations = userAndPermissionIdList.map(({ userId, permission }) => ({
      userId,
      permissionId: permission.id,
      data: { houseIds: Array.from(new Set([...propz.get(permission, ['houseIds'], []), ...houseIds])) as string[] }
    }));

    this.setState({
      isLoading: true
    });

    updatePermissionsInBulk(user, 'UPDATE', operations)
      .then(() => {
        this.setState({ selectedItems: [], isSelectAllChecked: false, excludedSelectionIds: [] });
        this.onCloseActionsPopupClick();
        this.setItems();
      })
      .catch(error => {
        console.error('Can not assign houses to staff', error);
        this.setState({ isLoading: false });
      });
  };

  getFormsFiltered() {
    const { selectedItems } = this.state;

    const permissions = Lazy(selectedItems)
      .map(item => item.permissions)
      .flatten()
      .filter(permission => !this.isProtectedRoleForManager(permission))
      .toArray();
    const allForms = Lazy(permissions)
      .map(permission => permission.forms || [])
      .flatten()
      .uniq('id')
      .toArray();

    const formsFiltered = allForms.filter(form => {
      return selectedItems.every(item =>
        item.permissions.some(
          permission =>
            !this.isProtectedRoleForManager(permission) &&
            permission.forms.some(permissionForm => permissionForm.id === form.id)
        )
      );
    });

    return formsFiltered;
  }

  getHousesFiltered() {
    const { selectedItems } = this.state;

    const permissions = Lazy(selectedItems)
      .map(item => item.permissions)
      .flatten()
      .filter(permission => !this.isProtectedRoleForManager(permission))
      .toArray();
    const allHouses = Lazy(permissions)
      .map(permission => permission.houses || [])
      .flatten()
      .uniq('id')
      .toArray();

    const housesFiltered = allHouses.filter(house => {
      return selectedItems.every(item =>
        item.permissions.some(
          permission =>
            !this.isProtectedRoleForManager(permission) &&
            permission.houses.some(permissionHouse => permissionHouse.id === house.id)
        )
      );
    });

    return housesFiltered;
  }

  getRolesFiltered() {
    const { selectedItems } = this.state;

    const permissions = Lazy(selectedItems)
      .map(item => item.permissions)
      .flatten()
      .filter(permission => !this.isProtectedRoleForManager(permission))
      .toArray();

    const allRoles = Lazy(permissions)
      .map(permission => permission.preset)
      .uniq()
      .toArray();

    const rolesFiltered = allRoles.filter(role => {
      return selectedItems.every(item =>
        item.permissions.some(permission => !this.isProtectedRoleForManager(permission) && permission.preset === role)
      );
    });

    return rolesFiltered;
  }

  onRemoveRoleClick = (role): void => {
    const { user } = this.props;
    const { selectedItems } = this.state;

    const operations = selectedItems.reduce<any[]>(
      (result, item) => [
        ...result,
        ...item.permissions
          .filter(permission => !this.isProtectedRoleForManager(permission) && permission.preset === role)
          .map(permission => ({ userId: item.id, permissionId: permission.id }))
      ],
      []
    );

    this.setState({
      isLoading: true
    });

    updatePermissionsInBulk(user, 'REMOVE', operations)
      .then(() => {
        this.setState({ selectedItems: [], isSelectAllChecked: false, excludedSelectionIds: [] });
        this.onCloseActionsPopupClick();
        this.setItems();
      })
      .catch(error => {
        console.error('Can not remove staff roles', error);
        this.setState({ isLoading: false });
      });
  };

  onRemoveFormClick = (form): void => {
    const { user } = this.props;
    const formId = form.id;
    const { selectedItems } = this.state;

    const operations = selectedItems.reduce<any[]>(
      (result, item) => [
        ...result,
        ...item.permissions
          .filter(
            permission =>
              !this.isProtectedRoleForManager(permission) &&
              permission.forms.some(permissionForm => permissionForm.id === formId)
          )
          .map(permission => ({
            userId: item.id,
            permissionId: permission.id,
            data: { formIds: permission.formIds.filter(permissionFormId => permissionFormId !== formId) }
          }))
      ],
      []
    );

    this.setState({
      isLoading: true
    });

    updatePermissionsInBulk(user, 'UPDATE', operations)
      .then(() => {
        this.setState({ selectedItems: [], isSelectAllChecked: false, excludedSelectionIds: [] });
        this.onCloseActionsPopupClick();
        this.setItems();
      })
      .catch(error => {
        console.error('Can not remove forms from staff', error);
        this.setState({ isLoading: false });
      });
  };

  onRemoveHouseClick = (house): void => {
    const { user } = this.props;
    const houseId = house.id;
    const { selectedItems } = this.state;

    const operations = selectedItems.reduce<any[]>(
      (result, item) => [
        ...result,
        ...item.permissions
          .filter(
            permission =>
              !this.isProtectedRoleForManager(permission) &&
              permission.houses.some(permissionHouse => permissionHouse.id === houseId)
          )
          .map(permission => ({
            userId: item.id,
            permissionId: permission.id,
            data: { houseIds: permission.houseIds.filter(permissionHouseId => permissionHouseId !== houseId) }
          }))
      ],
      []
    );

    this.setState({
      isLoading: true
    });

    updatePermissionsInBulk(user, 'UPDATE', operations)
      .then(() => {
        this.setState({ selectedItems: [], isSelectAllChecked: false, excludedSelectionIds: [] });
        this.onCloseActionsPopupClick();
        this.setItems();
      })
      .catch(error => {
        console.error('Can not remove houses from staff', error);
        this.setState({ isLoading: false });
      });
  };

  setSelectedMisExclusion = (isExcludedFromMisIntegration: boolean): void => {
    this.resolveSelectedStaffForBulkAction(() => {
      const { user } = this.props;
      const operations = staffMisOperations(this.state.selectedItems, user.activeSchoolId, user.activeRole, isExcludedFromMisIntegration);
      if (operations.length === 0) return;
      const affectedStaff = new Set(operations.map(operation => operation.userId)).size;
      if (
        !isExcludedFromMisIntegration &&
        !window.confirm(
          `Return ${affectedStaff} external staff member${affectedStaff === 1 ? '' : 's'} to MIS management? Only permissions marked External staff will change. The next MIS sync may update or remove those permissions.`
        )
      ) {
        return;
      }

      this.setState({ isLoading: true });
      updatePermissionsInBulk(user, 'UPDATE', operations)
        .then(() => {
          this.setState({ selectedItems: [], isSelectAllChecked: false, excludedSelectionIds: [] });
          this.setItems();
        })
        .catch(error => {
          console.error('Can not update external staff status', error);
          this.setState({ isLoading: false });
        });
    });
  };

  renderActionsModal(): React.ReactNode {
    const { user } = this.props;
    const { isListActionsModalOpen, activeListAction } = this.state;

    if (!isListActionsModalOpen) {
      return null;
    }

    switch (activeListAction) {
      case STAFF_LIST_ACTIONS.ASSIGN_FORM:
        return (
          <FormAssignModal
            user={user}
            onSubmitFormsClick={this.onAssignFormsClick}
            onCancelClick={this.onCloseActionsPopupClick}
            isOpen={isListActionsModalOpen}
          />
        );
      case STAFF_LIST_ACTIONS.ASSIGN_HOUSE:
        return (
          <HouseAssignModal
            user={user}
            onSubmitHousesClick={this.onAssignHouseClick}
            onCancelClick={this.onCloseActionsPopupClick}
            isOpen={isListActionsModalOpen}
          />
        );
      case STAFF_LIST_ACTIONS.REMOVE_FORM:
        const forms = this.getFormsFiltered();
        return (
          <FormRemoveModal
            user={user}
            onSubmitFormsClick={this.onRemoveFormClick}
            onCancelClick={this.onCloseActionsPopupClick}
            forms={forms}
            isOpen={isListActionsModalOpen}
          />
        );
      case STAFF_LIST_ACTIONS.REMOVE_HOUSE:
        const houses = this.getHousesFiltered();
        return (
          <HouseRemoveModal
            user={user}
            onSubmitHouseClick={this.onRemoveHouseClick}
            onCancelClick={this.onCloseActionsPopupClick}
            houses={houses}
            isOpen={isListActionsModalOpen}
          />
        );
      case STAFF_LIST_ACTIONS.REMOVE_ROLE:
        const roles = this.getRolesFiltered();
        return (
          <RoleRemoveModal
            user={user}
            onSubmitClick={this.onRemoveRoleClick}
            onCancelClick={this.onCloseActionsPopupClick}
            roles={roles}
            isOpen={isListActionsModalOpen}
          />
        );
    }
  }

  onUserTagChange = (event): void => {
    const value = event.target.value;

    this.setState({
      selectedUserTagId: value
    });
  };

  onUserTagSaveClick = (selectedTagIds: string[] = []): void => {
    const tagIds = selectedTagIds.length ? selectedTagIds : [this.state.selectedUserTagId].filter(Boolean);
    const user = this.props.user;

    this.setState({
      isLoading: true
    });

    if (tagIds.length === 0) {
      this.setState({
        isUserTagModalOpen: false,
        selectedUserTagId: ''
      });
      console.error('Can not assign user tag id');
    } else {
      const selectedItems = this.state.selectedItems;
      updateUserTagsInBulk(
        user,
        selectedItems.map(item => item.id),
        tagIds,
        'ADD'
      )
        .then(() => {
          this.setState({
            isUserTagModalOpen: false,
            selectedUserTagId: '',
            selectedItems: [],
            isSelectAllChecked: false,
            excludedSelectionIds: []
          });
          this.setItems();
        })
        .catch(error => {
          console.error('Can not assign staff tags', error);
          this.setState({ isLoading: false });
        });
    }
  };

  renderUserTagAssignModal(): React.ReactNode {
    const { tags, selectedUserTagId, isUserTagModalOpen, selectedItems } = this.state;
    const tagIdsByUser = selectedItems.map(item => this.getSafeStaffTags(item).map(tag => tag.id));
    const excludedUserTagIds = Array.from(
      new Set(tagIdsByUser.reduce<string[]>((result, tagIds) => [...result, ...tagIds], []))
    );

    return (
      <UserTagAssignModal
        modalTitle={'Assign User Tag'}
        userTags={tags}
        selectedUserTagId={selectedUserTagId}
        excludedUserTagIds={excludedUserTagIds}
        selectedStudents={selectedItems}
        onUserTagChange={this.onUserTagChange}
        onCloseClick={this.closeUserTagAssignModal}
        isOpen={isUserTagModalOpen}
        onCreateTag={this.onCreateUserTagFromAssignModal}
        onSaveClick={this.onUserTagSaveClick}
      />
    );
  }

  onCreateUserTagFromAssignModal = (tagName: string, color: string): Promise<Tag> => {
    return createUserTag(this.props.user, { tagName, color }).then((createdTag: Tag) => {
      this.setState(prevState => ({ tags: [...prevState.tags, createdTag] }));
      return createdTag;
    });
  };

  openUserTagAssignModal = (): void => {
    this.resolveSelectedStaffForBulkAction(() => {
      this.setState({
        isUserTagModalOpen: true
      });
    });
  };

  closeUserTagAssignModal = () => {
    this.setState({
      isUserTagModalOpen: false,
      selectedUserTagId: ''
    });
  };

  getUserTags(): Tag[] {
    const { selectedItems, items } = this.state;

    const tags = Lazy(selectedItems)
      .map(selectedItem => {
        const selectedItemId = selectedItem.id;
        const user = items.find(userState => userState.id === selectedItemId);

        if (typeof user !== 'undefined') {
          return user.permissions[0].tags;
        } else {
          return [];
        }
      })
      .filter(tagList => tagList.length > 0)
      .flatten()
      .uniq('id')
      .toArray();

    return tags;
  }

  onUserTagDelete = (selectedTagIds?: string[]): void => {
    const userTagIds =
      selectedTagIds && selectedTagIds.length ? selectedTagIds : [this.state.selectedUserTagId].filter(Boolean);
    const user = this.props.user;

    this.setState({
      isLoading: true
    });

    if (userTagIds.length === 0) {
      this.setState({
        isUserTagDeleteModalOpen: false,
        selectedUserTagId: ''
      });
      console.error('Can not assign user tag id');
    } else {
      const selectedItems = this.state.selectedItems;

      updateUserTagsInBulk(
        user,
        selectedItems.map(item => item.id),
        userTagIds,
        'REMOVE'
      )
        .then(() => {
          this.setState({
            isUserTagDeleteModalOpen: false,
            selectedUserTagId: '',
            selectedItems: [],
            isSelectAllChecked: false,
            excludedSelectionIds: []
          });
          this.setItems();
        })
        .catch(error => {
          console.error('Can not remove staff tags', error);
          this.setState({ isLoading: false });
        });
    }
  };

  renderUserTagDeleteModal(): React.ReactNode {
    const { selectedUserTagId, isUserTagDeleteModalOpen } = this.state;

    return (
      <UserTagDeleteModal
        modalTitle={'Delete User Tag'}
        userTags={this.getUserTags()}
        selectedUserTagId={selectedUserTagId}
        onUserTagChange={this.onUserTagChange}
        onCloseClick={this.closeUserTagDeleteModal}
        isOpen={isUserTagDeleteModalOpen}
        onDeleteClick={this.onUserTagDelete}
      />
    );
  }

  openUserTagDeleteModal = () => {
    this.resolveSelectedStaffForBulkAction(() => {
      this.setState({
        isUserTagDeleteModalOpen: true
      });
    });
  };

  closeUserTagDeleteModal = () => {
    this.setState({
      isUserTagDeleteModalOpen: false,
      selectedUserTagId: ''
    });
  };

  getStaffFullName(staff: SchoolUser): string {
    return `${propz.get(staff, ['firstName'], '')} ${propz.get(staff, ['lastName'], '')}`.trim();
  }

  getStaffInitials(staff: SchoolUser): string {
    return `${propz.get(staff, ['firstName'], '?').charAt(0)}${propz.get(staff, ['lastName'], '').charAt(0)}`;
  }

  renderStaffAvatar(staff: SchoolUser, isLarge: boolean = false): React.ReactNode {
    const avatar = propz.get(staff, ['avatar'], '');
    const className = isLarge
      ? 'people-avatar people-avatar--lg staff-avatar--image'
      : 'people-avatar staff-avatar--image';

    if (avatar) {
      return <span className={className} style={{ backgroundImage: `url(${avatar})` }} />;
    }

    return (
      <span className={isLarge ? 'people-avatar people-avatar--lg' : 'people-avatar'}>
        {this.getStaffInitials(staff)}
      </span>
    );
  }

  renderStaffChipList(values: string[], type: 'role' | 'sport' | 'neutral'): React.ReactNode {
    const visibleValues = values.slice(0, 2);
    const moreCount = Math.max(values.length - visibleValues.length, 0);

    if (visibleValues.length === 0) {
      return <span className="people-flag people-flag--off">-</span>;
    }

    return (
      <span className="staff-modernChips">
        {visibleValues.map(value => (
          <span key={`${type}_${value}`} className={`staff-modernChip staff-modernChip--${type}`}>
            {type === 'role' ? normalizeStaffRoleName(value) : value}
          </span>
        ))}
        {moreCount > 0 && <span className="staff-modernChipMore">+{moreCount} more</span>}
      </span>
    );
  }

  renderStaffOptionChips(values: string): React.ReactNode {
    const chips = String(values || '')
      .split(',')
      .map(item => item.trim())
      .filter(item => item !== '');

    return this.renderStaffChipList(chips, 'neutral');
  }

  getSafeStaffTags(staff: SchoolUser): Tag[] {
    return Lazy(propz.get(staff, ['permissions'], []))
      .map(permission => propz.get(permission, ['tags'], []))
      .flatten()
      .filter(tag => typeof tag !== 'undefined' && tag !== null)
      .uniq('id')
      .toArray() as Tag[];
  }

  renderStaffTags(staff: SchoolUser): React.ReactNode {
    const rawTags = this.getSafeStaffTags(staff);

    if (rawTags.length === 0) {
      return <span className="people-flag people-flag--off">-</span>;
    }

    return (
      <span className="people-tags">
        {rawTags.slice(0, 2).map(tag => {
          const color = getUserTagColor(tag);
          return (
            <span key={tag.id || tag.tagName} style={{ '--tag-color': color } as React.CSSProperties}>
              <i />
              {tag.tagName}
            </span>
          );
        })}
        {rawTags.length > 2 && <span className="staff-modernChipMore">+{rawTags.length - 2} more</span>}
      </span>
    );
  }

  renderStaffTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      isSelectAllChecked,
      excludedSelectionIds,
      previewStaff,
      filters,
      currentPage,
      total,
      forms,
      houses,
      tags,
      hiddenStaffColumnKeys,
      isColumnsMenuOpen,
      isSelectionMenuOpen
    } = this.state;

    const hiddenColumns = new Set(hiddenStaffColumnKeys);
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const excludedIds = new Set(excludedSelectionIds);
    const previewStaffId = previewStaff ? previewStaff.id : '';
    const allPageSelected = isSelectAllChecked
      ? items.length > 0 && items.every(item => !excludedIds.has(item.id))
      : items.length > 0 && items.every(item => selectedIds.has(item.id));
    const selectedCount = isSelectAllChecked ? Math.max(0, total - excludedSelectionIds.length) : selectedItems.length;
    const misSelection = isSelectAllChecked ? selectedItems.filter(item => !excludedSelectionIds.includes(item.id)) : selectedItems;
    const canMarkExternal = staffMisOperations(misSelection, this.props.user.activeSchoolId, this.props.user.activeRole, true).length > 0;
    const canReturnToMis = staffMisOperations(misSelection, this.props.user.activeSchoolId, this.props.user.activeRole, false).length > 0;
    const filterOptions = {
      role: getSelectOptionForStaffs(),
      userStatus: getSelectOptionForUserStatus(),
      permissionFormIds: getSelectOptionForForms(forms),
      permissionHouseIds: getSelectOptionForHouses(houses),
      tag: getSelectOptionForTag(tags)
    };

    const lockedColumns = new Set(['firstName', 'lastName']);
    const visibleColumns = COLUMNS.filter(
      column => lockedColumns.has(column.field) || !hiddenColumns.has(column.field)
    );

    const getFilterValue = (field: string): string => {
      const value = filters[field];
      if (Array.isArray(value)) {
        return value[0] || '';
      }
      if (value && typeof value === 'object') {
        return '';
      }
      return typeof value === 'undefined' ? '' : String(value);
    };

    const renderSelect = (field: string, label: string, options: any[], placeholder: string): React.ReactNode => (
      <label className="people-filter__field">
        <span>{label}</span>
        <select
          className={
            getFilterValue(field) === ''
              ? 'staff-filter__select staff-filter__select--placeholder'
              : 'staff-filter__select'
          }
          value={getFilterValue(field)}
          onChange={event => this.onModernFilterChange(field, event.target.value)}
          onKeyDown={this.onModernFilterKeyDown}
        >
          <option value="">{placeholder}</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.text || option.label || option.value}
            </option>
          ))}
        </select>
      </label>
    );

    const renderMultiSelect = (
      field: 'role' | 'userStatus' | 'tag' | 'permissionFormIds' | 'permissionHouseIds',
      label: string,
      options: any[]
    ): React.ReactNode => {
      const selected = Array.isArray(filters[field]) ? filters[field] : filters[field] ? [filters[field]] : [];
      const tagById = new Map(tags.map(tag => [String(tag.id), tag]));
      if (field === 'permissionFormIds') {
        return (
          <SearchMultiSelectFilter
            label={label}
            selected={selected.map(String)}
            options={options.map(option => ({
              value: String(option.value),
              label: String(option.text || option.label || option.value)
            }))}
            onToggle={value => this.onModernMultiFilterToggle(field, value)}
            placeholder="Search registration forms…"
          />
        );
      }
      return (
        <PeopleFilterChipSelect
          label={label}
          selected={selected.map(String)}
          collapsedLimit={field === 'tag' || field === 'permissionHouseIds' ? 6 : undefined}
          options={options.map(option => {
            const value = String(option.value);
            return {
              value,
              label: String(option.text || option.label || value),
              color: field === 'tag' ? getUserTagColor(tagById.get(value)) : undefined
            };
          })}
          onToggle={value => this.onModernMultiFilterToggle(field, value)}
        />
      );
    };

    const renderSearchField = (field: string, label: string, placeholder: string): React.ReactNode => (
      <label className="people-search__field">
        <span>{label}</span>
        <input
          value={getFilterValue(field)}
          onChange={event => this.onModernFilterChange(field, event.target.value)}
          onKeyDown={this.onModernFilterKeyDown}
          placeholder={placeholder}
        />
      </label>
    );

    const renderFilterText = (field: 'email' | 'phone', label: string, placeholder: string): React.ReactNode => (
      <label className="people-filter__field">
        <span>{label}</span>
        <input
          value={getFilterValue(field)}
          placeholder={placeholder}
          onChange={event => this.onModernFilterChange(field, event.target.value)}
          onKeyDown={this.onModernFilterKeyDown}
        />
      </label>
    );

    const getCellValue = (staff: SchoolUser, column: ColumnDefinition): React.ReactNode => {
      const field = column.field;

      switch (field) {
        case 'firstName':
          return (
            <div className="people-person">
              {this.renderStaffAvatar(staff)}
              <span className="people-person__name">{propz.get(staff, ['firstName'], '')}</span>
            </div>
          );
        case 'lastName':
          return <span className="people-strong">{propz.get(staff, ['lastName'], '')}</span>;
        case 'email':
          return <span className="people-mutedText">{getEmailOrMisEmail(staff) || '-'}</span>;
        case 'phone':
          return <span className="people-mutedText">{getPhoneOrMisPhone(staff) || '-'}</span>;
        case 'role':
          return this.renderStaffChipList(getStaffRoleNames(staff), 'role');
        case 'sports':
          return this.renderStaffChipList(getStaffSportNames(staff, this.state.sports), 'sport');
        case 'permissionFormIds':
          return this.renderStaffOptionChips(getUserForms(staff));
        case 'permissionHouseIds':
          return this.renderStaffOptionChips(getUserHouses(staff));
        case 'tag':
          return this.renderStaffTags(staff);
        default:
          const accessor = (column as any).accessor;
          if (typeof accessor === 'function') {
            return accessor(staff);
          }
          if (Array.isArray(accessor)) {
            return propz.get(staff, accessor, '-') || '-';
          }
          return propz.get(staff, [field], '-') || '-';
      }
    };

    const toggleAllOnPage = (): void => {
      if (allPageSelected) {
        const pageIds = new Set(items.map(item => item.id));
        if (isSelectAllChecked) {
          this.setState({
            excludedSelectionIds: Array.from(new Set([...excludedSelectionIds, ...Array.from(pageIds)])),
            isSelectionMenuOpen: false
          });
          return;
        }
        this.setState({
          selectedItems: selectedItems.filter(item => !pageIds.has(item.id)),
          isSelectAllChecked: false,
          isSelectionMenuOpen: false
        });
      } else {
        if (isSelectAllChecked) {
          this.setState({
            excludedSelectionIds: excludedSelectionIds.filter(id => !items.some(item => item.id === id)),
            isSelectionMenuOpen: false
          });
          return;
        }
        const next = [...selectedItems];
        items.forEach(item => {
          if (!selectedIds.has(item.id)) {
            next.push(item);
          }
        });
        this.setState({ selectedItems: next, isSelectAllChecked: false, isSelectionMenuOpen: false });
      }
    };

    return (
      <div className="people-screen staff-modernScreen schoolPeopleListCard">
        <div className="people-toolbar">
          <div className="people-toolbar__heading">
            <div className="people-search people-search--name staff-toolbarSearch">
              {renderSearchField('nameSearch', 'Name', 'Search by first or last name')}
            </div>
            <button
              type="button"
              className={`people-btn people-btn--secondary people-btn--iconLeft people-filterBtn ${
                isShowFilter ? 'people-btn--on' : ''
              }`}
              onClick={this.onTableFilterClick}
            >
              <span className="people-icon people-icon--filter" />
              Filters
              {countActivePeopleFilters(filters) > 0 ? (
                <span className="people-btn__badge">{countActivePeopleFilters(filters)}</span>
              ) : null}
            </button>
            <div className="people-columns">
              <button
                type="button"
                className="people-btn people-btn--secondary people-btn--iconLeft"
                onClick={() => this.setState({ isColumnsMenuOpen: !isColumnsMenuOpen })}
              >
                <span className="people-icon people-icon--columns" />
                Columns <span className="people-btn__badge">{visibleColumns.length}</span>
              </button>
              {isColumnsMenuOpen && (
                <div className="people-columns__menu">
                  <div className="people-columns__head">
                    <span>Show / hide columns</span>
                    <button type="button" onClick={this.resetStaffColumns}>
                      Reset
                    </button>
                  </div>
                  {COLUMNS.map(column => {
                    const isLocked = lockedColumns.has(column.field);
                    const isVisible = isLocked || !hiddenColumns.has(column.field);
                    return (
                      <label
                        key={column.field}
                        className={`people-columns__item ${isLocked ? 'people-columns__item--locked' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isVisible}
                          disabled={isLocked}
                          onChange={() => this.toggleStaffColumn(column.field)}
                        />
                        <span>{column.text}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {isShowFilter && (
          <form
            className="people-filter staff-filter"
            onSubmit={event => {
              event.preventDefault();
              this.onApplyFilterClick();
            }}
          >
            <div className="people-filter__header">
              <strong>Filters</strong>
              <button
                type="button"
                onClick={() => {
                  this.setState({ isShowFilter: false });
                  this.setItems();
                }}
                aria-label="Close filters"
              >
                ×
              </button>
            </div>
            <div className="people-filter__body">
              {renderFilterText('email', 'Email', 'Search by email')}
              {renderFilterText('phone', 'Phone', 'Search by phone')}
              {renderMultiSelect('role', 'Roles', filterOptions.role)}
              {renderMultiSelect('userStatus', 'Status', filterOptions.userStatus)}
              {renderMultiSelect('permissionFormIds', 'Registration forms', filterOptions.permissionFormIds)}
              {renderMultiSelect('permissionHouseIds', 'Houses', filterOptions.permissionHouseIds)}
              {renderMultiSelect('tag', 'Tags', filterOptions.tag)}
            </div>
            <div className="people-filter__actions">
              <button type="button" className="people-btn people-btn--ghost" onClick={this.onClearFilterClick}>
                Clear all
              </button>
              <div>
                <button
                  type="button"
                  className="people-btn people-btn--secondary"
                  onClick={() => {
                    this.setState({ isShowFilter: false });
                    this.setItems();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="people-btn people-btn--primary">
                  Apply
                </button>
              </div>
            </div>
          </form>
        )}

        <PeopleFilterChips
          filters={filters}
          columns={FILTER_COLUMNS}
          valueLabels={{
            permissionFormIds: filterOptions.permissionFormIds.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            permissionHouseIds: filterOptions.permissionHouseIds.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            role: filterOptions.role.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            userStatus: filterOptions.userStatus.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {}),
            tag: filterOptions.tag.reduce((result, option) => {
              result[String(option.value)] = option.text || String(option.value);
              return result;
            }, {})
          }}
          onRemove={field => this.setState({ filters: { ...filters, [field]: '' } }, this.onApplyFilterClick)}
          onClear={this.onClearFilterClick}
        />

        {selectedCount > 0 && (
          <div className="people-selection">
            <span className="people-selection__summary">{selectedCount} selected</span>
            <div className="people-selection__actions">
              <button type="button" className="people-btn people-btn--secondary" onClick={this.onSendMessageClick}>
                <span aria-hidden="true">+</span> Create message
              </button>
              <button type="button" className="people-btn people-btn--secondary" onClick={this.openUserTagAssignModal}>
                Assign tag
              </button>
              <button type="button" className="people-btn people-btn--secondary" onClick={this.openUserTagDeleteModal}>
                Remove tag
              </button>
              <button
                type="button"
                className="people-btn people-btn--secondary"
                disabled={selectedCount !== 1}
                onClick={this.onAddRoleClick}
              >
                <span aria-hidden="true">+</span> Add role
              </button>
              <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={() => this.onListActionClick(STAFF_LIST_ACTIONS.ASSIGN_FORM)}
              >
                Assign form
              </button>
              <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={() => this.onListActionClick(STAFF_LIST_ACTIONS.ASSIGN_HOUSE)}
              >
                Assign house
              </button>
              <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={() => this.onListActionClick(STAFF_LIST_ACTIONS.REMOVE_ROLE)}
              >
                Remove role
              </button>
              {canMarkExternal && <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={() => this.setSelectedMisExclusion(true)}
              >
                Mark as external staff
              </button>}
              {canReturnToMis && <button
                type="button"
                className="people-btn people-btn--secondary"
                onClick={() => this.setSelectedMisExclusion(false)}
              >
                Return to MIS management
              </button>}
            </div>
            <div className="people-toolbar__spacer" />
            <button type="button" className="people-selection__clear" onClick={this.onUnselectAllClick}>
              Clear selection
            </button>
          </div>
        )}

        <div className="people-tableCard">
          <div className="people-tableScroll">
            <table className="people-table staff-modernTable">
              <thead>
                <tr>
                  <th className="people-table__check">
                    <div className="people-bulkSelect">
                      <button
                        type="button"
                        className={`people-bulkSelect__trigger ${
                          allPageSelected ? 'people-bulkSelect__trigger--checked' : ''
                        }`}
                        aria-label="Bulk select staff"
                        aria-expanded={isSelectionMenuOpen}
                        onClick={event => {
                          event.stopPropagation();
                          this.setState({ isSelectionMenuOpen: !isSelectionMenuOpen });
                        }}
                      >
                        <span className="people-bulkSelect__box" />
                      </button>
                      {isSelectionMenuOpen && (
                        <div className="people-bulkSelect__menu">
                          <button type="button" onClick={toggleAllOnPage}>
                            Select this page
                          </button>
                          <button type="button" onClick={this.onSelectAllClick}>
                            Select all
                          </button>
                          <button type="button" disabled={selectedCount === 0} onClick={this.onUnselectAllClick}>
                            Clear selection
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                  {visibleColumns.map(column => {
                    const isSorted = sortColumnsName === column.field;
                    return (
                      <th
                        key={column.field}
                        onClick={column.isSort ? () => this.onTableSortClick(column.field) : undefined}
                        className={column.isSort ? 'people-table__sortable' : ''}
                      >
                        <span>{column.text}</span>
                        {column.isSort && (
                          <i className={`people-sort ${isSorted ? `people-sort--${sortDirection}` : ''}`} />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td className="people-empty" colSpan={visibleColumns.length + 1}>
                      <ListEmptyState
                        icon={faUserTie}
                        title={isDataFiltered ? 'No staff match your filters' : 'No staff yet'}
                        description={
                          isDataFiltered
                            ? 'Try changing or clearing the search and filters to see more staff members.'
                            : 'Teachers, coaches, managers and administrators will appear here.'
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  items.map(staff => {
                    const isSelected = isSelectAllChecked ? !excludedIds.has(staff.id) : selectedIds.has(staff.id);
                    const isPreviewed = previewStaffId === staff.id;
                    return (
                      <tr
                        key={staff.id}
                        data-selected={isSelected}
                        data-preview={isPreviewed}
                        onClick={() => this.onStaffPreviewClick(staff)}
                      >
                        <td className="people-table__check" onClick={event => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={event => this.onStaffRowCheckboxClick(event, staff)}
                          />
                        </td>
                        {visibleColumns.map(column => (
                          <td key={column.field}>{getCellValue(staff, column)}</td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {total > 0 ? (
          <Pagination2
            modern
            currentPage={currentPage}
            total={total}
            setCurrentPageParams={this.setCurrentPageParams}
          />
        ) : null}
      </div>
    );
  }
  isOneItemSelected(): boolean {
    const selectedStaffs = this.state.selectedItems;
    return selectedStaffs.length === 1;
  }

  onSendMessageClick = (): void => {
    this.resolveSelectedStaffForBulkAction(() => {
      const activeSchoolId = this.props.user.activeSchoolId;
      const staffUsers = this.state.selectedItems
        .map(schoolUser => ({
          ...schoolUser,
          permissions: propz.get(schoolUser, ['permissions'], []).filter(permission => {
            const schoolId = propz.get(permission, ['schoolId']);
            return (
              STAFF_LIST.indexOf(propz.get(permission, ['preset'])) !== -1 &&
              propz.get(permission, ['status']) === 'ACTIVE' &&
              (!schoolId || !activeSchoolId || schoolId.toString() === activeSchoolId.toString())
            );
          })
        }))
        .filter(schoolUser => schoolUser.permissions.length > 0);

      this.props.history.push({
        pathname: '/generalMessages/create',
        state: {
          fromAllUsersList: true,
          users: staffUsers,
          returnTo: {
            pathname: this.state.basePath,
            search: this.props.location.search
          }
        }
      });
    });
  };

  onAddRoleClick = (): void => {
    this.setState({
      isAddRoleModalOpen: true,
      editablePermissionId: ''
    });
  };

  onListActionClick = (actionType: STAFF_LIST_ACTIONS): void => {
    this.resolveSelectedStaffForBulkAction(() => {
      this.setState({
        isListActionsModalOpen: true,
        activeListAction: actionType
      });
    });
  };

  renderSummary(): React.ReactNode {
    const { currentTabIndex, previewStaff } = this.state;

    if (!previewStaff) {
      return null;
    }

    const fullName = this.getStaffFullName(previewStaff);
    const roles =
      getStaffRoleNames(previewStaff)
        .map(role => normalizeStaffRoleName(role))
        .join(', ') || 'No role';
    const email = getEmailOrMisEmail(previewStaff) || 'No email';

    return (
      <>
        <div className="people-drawerScrim" onClick={this.closeStaffPreview} />
        <aside className="people-drawer" role="dialog" aria-modal="true">
          <div className="people-drawer__head">
            {this.renderStaffAvatar(previewStaff, true)}
            <div className="people-drawer__identity">
              <h2>{fullName}</h2>
              <div>
                <span>{roles}</span>
                <i />
                <span>{email}</span>
              </div>
            </div>
            <button type="button" className="people-drawer__close" onClick={this.closeStaffPreview} aria-label="Close">
              ×
            </button>
          </div>

          <div className="people-drawer__tabs">
            {MEMBER_OF_STAFF_TABS.map((tab, index) => (
              <button
                key={tab}
                type="button"
                className={currentTabIndex === index ? 'active' : ''}
                onClick={event => this.onSummaryTabClick(event, index)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="people-drawer__body">{this.getStaffSummaryTabs()}</div>

          <div className="people-drawer__foot staff-drawerFoot">
            <button
              type="button"
              className="people-btn people-btn--secondary"
              onClick={() => this.usePreviewStaffForAction(this.onSendMessageClick)}
            >
              <FontAwesomeIcon icon={faEnvelope} /> Message
            </button>
            <button
              type="button"
              className="people-btn people-btn--secondary"
              onClick={() => this.usePreviewStaffForAction(this.openUserTagAssignModal)}
            >
              <FontAwesomeIcon icon={faTags} /> Assign tag
            </button>
            <button
              type="button"
              className="people-btn people-btn--primary"
              onClick={() => this.setState({ currentTabIndex: 1 })}
            >
              <FontAwesomeIcon icon={faUserShield} /> Manage access
            </button>
          </div>
        </aside>
      </>
    );
  }

  onSummaryTabClick = (event: any, index: number): void => {
    event.preventDefault();
    this.setState({
      currentTabIndex: index
    });
  };

  getStaffPermissionsForEditor(staff: SchoolUser): any[] {
    return propz.get(staff, ['permissions'], []).filter(permission => STAFF_LIST.indexOf(permission.preset) !== -1);
  }

  isProtectedRoleForManager(permission: any): boolean {
    return (
      this.props.user.activeRole === ROLE.MANAGER &&
      (permission.preset === ROLE.ADMIN || permission.preset === ROLE.MANAGER || permission.preset === ROLE.SLT)
    );
  }

  getPrimaryStaffPermission(staff: SchoolUser): any {
    const permissions = this.getStaffPermissionsForEditor(staff);
    return permissions.find(permission => !this.isProtectedRoleForManager(permission));
  }

  getSportNameById(sportId: any): string {
    if (sportId && typeof sportId === 'object' && sportId.name) return sportId.name;
    const normalizedId = String(propz.get(sportId, ['id'], propz.get(sportId, ['_id'], sportId)) || '');
    const sport = this.state.sports.find(
      item => String(propz.get(item, ['id'], propz.get(item, ['_id'], ''))) === normalizedId
    );
    return sport ? sport.name : normalizedId;
  }

  getHouseColorById(houseId: string): string {
    const house = this.getHouseById(houseId);
    const colors = propz.get(house, ['colors'], []);
    const color = Array.isArray(colors) && colors.length > 0 ? colors[0] : propz.get(house, ['color'], '');
    return color || '#227ebd';
  }

  getHouseById(houseId: any): any {
    if (houseId && typeof houseId === 'object' && (houseId.name || houseId.houseName)) {
      return houseId;
    }
    const normalizedId = String(propz.get(houseId, ['id'], propz.get(houseId, ['_id'], houseId)) || '');
    return this.state.houses.find(
      item => String(propz.get(item, ['id'], propz.get(item, ['_id'], ''))) === normalizedId
    );
  }

  getHouseNameById(houseId: any): string {
    const house = this.getHouseById(houseId);
    return propz.get(house, ['name'], propz.get(house, ['houseName'], '')) || String(houseId || 'Unknown house');
  }

  updateStaffPermissionInState(staffId: string, permission: any): void {
    const updateStaff = (staff: SchoolUser): SchoolUser => {
      const permissions = [...propz.get(staff, ['permissions'], [])];
      const permissionIndex = permissions.findIndex(item => item.id === permission.id);

      if (permissionIndex === -1) {
        permissions.push({ tags: [], ...permission });
      } else {
        permissions.splice(permissionIndex, 1, { tags: [], ...permission });
      }

      return { ...staff, permissions };
    };

    this.setState(prevState => ({
      previewStaff:
        prevState.previewStaff && prevState.previewStaff.id === staffId
          ? updateStaff(prevState.previewStaff)
          : prevState.previewStaff,
      selectedItems: prevState.selectedItems.map(staff => (staff.id === staffId ? updateStaff(staff) : staff)),
      items: prevState.items.map(staff => (staff.id === staffId ? updateStaff(staff) : staff))
    }));
  }

  removeStaffPermissionFromState(staffId: string, permissionId: string): void {
    const updateStaff = (staff: SchoolUser): SchoolUser => ({
      ...staff,
      permissions: propz.get(staff, ['permissions'], []).filter(permission => permission.id !== permissionId)
    });

    this.setState(prevState => ({
      previewStaff:
        prevState.previewStaff && prevState.previewStaff.id === staffId
          ? updateStaff(prevState.previewStaff)
          : prevState.previewStaff,
      selectedItems: prevState.selectedItems.map(staff => (staff.id === staffId ? updateStaff(staff) : staff)),
      items: prevState.items.map(staff => (staff.id === staffId ? updateStaff(staff) : staff))
    }));
  }

  onInlineRoleAdd = (roleId: string): void => {
    const staff = this.state.previewStaff;

    if (!staff || !roleId) {
      return;
    }

    this.setState({ isLoading: true });

    const data =
      roleId === ROLE.TEACHER || roleId === ROLE.COACH
        ? { preset: roleId, formIds: [], houseIds: [], sportIds: [] }
        : { preset: roleId, formIds: [], houseIds: [] };

    postUserPermission(this.props.user, staff.id, data as any)
      .then(permission => {
        this.updateStaffPermissionInState(staff.id, permission);
        this.setState({ isLoading: false });
      })
      .catch(error => {
        console.error(error);
        this.setState({ isLoading: false });
      });
  };

  onInlineRoleRemove = (permission: any): void => {
    const staff = this.state.previewStaff;

    if (!staff || this.isProtectedRoleForManager(permission)) {
      return;
    }

    this.setState({ inlineRevokePermission: permission });
  };

  confirmInlineRoleRemove = (): void => {
    const { previewStaff: staff, inlineRevokePermission: permission } = this.state;
    if (!staff || !permission || this.isProtectedRoleForManager(permission)) return;
    this.setState({ isLoading: true });
    deleteDataToPermission(this.props.user, staff.id, permission.id).then(() => {
      this.removeStaffPermissionFromState(staff.id, permission.id);
      this.setState(prevState => ({
        previewStaff:
          prevState.previewStaff && propz.get(prevState.previewStaff, ['permissions'], []).length > 0
            ? prevState.previewStaff
            : null,
        inlineRevokePermission: null,
        isLoading: false
      }));
    });
  };

  renderInlineRevokeRoleModal(): React.ReactNode {
    const { inlineRevokePermission: permission, previewStaff } = this.state;
    if (!permission || !previewStaff) return null;
    const isOnlyRole = propz.get(previewStaff, ['permissions'], []).length === 1;
    return (
      <SimpleModal
        isOpen
        title="Revoke role?"
        customClass="peopleDangerModal"
        dialogStyle={{ maxWidth: 500 }}
        zIndex={2147483100}
        showFooter={false}
        isBodyBold={false}
        onCloseClick={() => this.setState({ inlineRevokePermission: null })}
      >
        <div className="people-delete people-roleRevoke">
          <div className="people-delete__alert people-delete__alert--noIcon">
            <div>
              <h3>{isOnlyRole ? 'This is the user’s only role.' : 'Access will change immediately.'}</h3>
              <p>The role “{normalizeStaffRoleName(permission.preset)}” will be revoked.</p>
            </div>
          </div>
          <div className="people-delete__details">
            <p>
              {isOnlyRole
                ? "The user will disappear from your school's user list and must request access again before you can restore it."
                : 'The user will keep any other roles they hold at this school.'}
            </p>
          </div>
          <div className="people-delete__footer">
            <button
              type="button"
              className="sit-btn sit-btn--ghost"
              onClick={() => this.setState({ inlineRevokePermission: null })}
            >
              Cancel
            </button>
            <button type="button" className="sit-btn sit-btn--danger" onClick={this.confirmInlineRoleRemove}>
              Revoke role
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  patchInlinePermission(permission: any, data: any): void {
    const staff = this.state.previewStaff;

    if (!staff || this.isProtectedRoleForManager(permission)) {
      return;
    }

    this.setState({ isLoading: true });
    putDataToPermission(this.props.user, staff.id, permission.id, permission.preset === ROLE.SLT ? data : { ...permission, ...data }).then(
      permissionUpdated => {
        this.updateStaffPermissionInState(staff.id, permissionUpdated);
        this.setState({ isLoading: false });
      }
    );
  }

  addIdToPermission(permission: any, field: 'formIds' | 'houseIds' | 'sportIds', id: string): void {
    if (!id) {
      return;
    }

    const values = propz.get(permission, [field], []);
    if (values.indexOf(id) !== -1) {
      return;
    }

    this.patchInlinePermission(permission, { [field]: [...values, id] });
  }

  removeIdFromPermission(permission: any, field: 'formIds' | 'houseIds' | 'sportIds', id: string): void {
    const values = propz.get(permission, [field], []);
    const normalizedId = String(propz.get(id as any, ['id'], propz.get(id as any, ['_id'], id)) || '');
    this.patchInlinePermission(permission, {
      [field]: values.filter(
        item => String(propz.get(item, ['id'], propz.get(item, ['_id'], item)) || '') !== normalizedId
      )
    });
  }

  addIdsToPermission(permission: any, field: 'formIds' | 'houseIds' | 'sportIds', ids: string[]): void {
    const values = propz.get(permission, [field], []);
    const existingIds = new Set(
      values.map(value => String(propz.get(value, ['id'], propz.get(value, ['_id'], value))))
    );
    const nextValues = [...values, ...ids.filter(id => !existingIds.has(String(id)))];
    this.patchInlinePermission(permission, { [field]: nextValues });
  }

  renderInlineAddSelect(
    label: string,
    options: Array<{ id: string; name: string }>,
    onChange: (id: string) => void
  ): React.ReactNode {
    if (options.length === 0) {
      return null;
    }

    return (
      <select
        className="staff-accessAddSelect"
        value=""
        onChange={event => {
          onChange(event.target.value);
          event.target.value = '';
        }}
      >
        <option value="" disabled hidden>
          {label}
        </option>
        {options.map(option => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  }

  renderRemovableChip(
    label: string,
    onRemove: () => void,
    color?: string,
    isRemovable: boolean = true
  ): React.ReactNode {
    return (
      <span className="staff-accessChip" style={{ '--staff-chip-color': color || '#227ebd' } as React.CSSProperties}>
        <i />
        <span>{label}</span>
        {isRemovable ? (
          <button type="button" onClick={onRemove} aria-label={`Remove ${label}`}>
            ×
          </button>
        ) : null}
      </span>
    );
  }

  renderStaffAccessEditor(staff: SchoolUser): React.ReactNode {
    const permissions = this.getStaffPermissionsForEditor(staff);
    const primaryPermission = this.getPrimaryStaffPermission(staff);
    const assignedRoleNames = permissions.map(permission => permission.preset);
    const roleOptions = STAFF_LIST.filter(
      role =>
        assignedRoleNames.indexOf(role) === -1 &&
        isSchoolRoleEnabled(this.props.user.activeSchool, role) &&
        (this.props.user.activeRole !== ROLE.MANAGER || (role !== ROLE.ADMIN && role !== ROLE.MANAGER && role !== ROLE.SLT))
    ).map(role => ({
      id: role,
      name: normalizeStaffRoleName(role)
    }));

    const formIds = propz.get(primaryPermission, ['formIds'], []);
    const houseIds = propz.get(primaryPermission, ['houseIds'], []);
    const assignedHouseIds = new Set(
      houseIds.map(houseId => String(propz.get(houseId, ['id'], propz.get(houseId, ['_id'], houseId)) || ''))
    );
    const formOptions = this.state.forms
      .filter(form => formIds.indexOf(form.id) === -1)
      .map(form => ({ id: form.id, name: form.name }));
    const houseOptions = this.state.houses
      .filter(house => !assignedHouseIds.has(String(propz.get(house, ['id'], propz.get(house, ['_id'], '')))))
      .map(house => ({ id: house.id, name: house.name }));

    return (
      <div className="staff-accessEditor">
        <section className="staff-accessSection">
          <div className="staff-accessSection__head">
            <span>Roles</span>
            {this.renderInlineAddSelect('+ Add role', roleOptions, roleId => this.onInlineRoleAdd(roleId))}
          </div>
          <div className="staff-roleCards">
            {permissions.length === 0 ? <span className="staff-accessEmpty">No roles assigned yet.</span> : null}
            {permissions.map(permission => {
              const isSportRole = permission.preset === ROLE.COACH || permission.preset === ROLE.TEACHER;
              const sportIds = propz.get(permission, ['sportIds'], []);
              const assignedSportIds = new Set(
                sportIds.map(sportId => String(propz.get(sportId, ['id'], propz.get(sportId, ['_id'], sportId)) || ''))
              );
              const sportOptions = this.state.sports
                .filter(sport => !assignedSportIds.has(String(propz.get(sport, ['id'], propz.get(sport, ['_id'], '')))))
                .map(sport => ({
                  id: String(propz.get(sport, ['id'], propz.get(sport, ['_id'], ''))),
                  name: sport.name
                }));
              return (
                <div key={permission.id} className="staff-roleCard">
                  <div className="staff-roleCard__top">
                    {this.renderRemovableChip(
                      normalizeStaffRoleName(permission.preset),
                      () => this.onInlineRoleRemove(permission),
                      '#227ebd',
                      !this.isProtectedRoleForManager(permission)
                    )}
                    {permission.isExcludedFromMisIntegration === true ? (
                      <span className="staff-modernChip staff-modernChip--neutral">External staff</span>
                    ) : null}
                  </div>
                  {permission.status ? (
                    <div className="staff-roleCard__meta">
                      <span className={`staff-roleStatus staff-roleStatus--${String(permission.status).toLowerCase()}`}>
                        {permission.status}
                      </span>
                    </div>
                  ) : null}
                  {permission.preset === ROLE.SLT ? <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id={`slt-students-${permission.id}`} checked={permission.sltAccess && permission.sltAccess.studentDrilldown === true} disabled={this.isProtectedRoleForManager(permission)} onChange={event => this.patchInlinePermission(permission, {sltAccess: {studentDrilldown: event.target.checked}})} />
                    <label className="form-check-label" htmlFor={`slt-students-${permission.id}`}>Allow individual student lists and evidence</label>
                    <p className="small text-muted mt-2">School-wide analytics. Student details are restricted by default.</p>
                  </div> : <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      id={`external-staff-${permission.id}`}
                      type="checkbox"
                      checked={permission.isExcludedFromMisIntegration === true}
                      disabled={this.isProtectedRoleForManager(permission)}
                      onChange={event => {
                        if (
                          event.target.checked ||
                          window.confirm(
                            'Return this permission to MIS management? The next MIS sync may update or remove it.'
                          )
                        ) {
                          this.patchInlinePermission(permission, {
                            isExcludedFromMisIntegration: event.target.checked
                          });
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor={`external-staff-${permission.id}`}>
                      External staff (ignored by MIS)
                    </label>
                  </div>}
                  {isSportRole ? (
                    <div className="staff-roleCard__sports">
                      <span className="staff-accessLabel">Activities/Sports</span>
                      <div className="staff-accessChips">
                        {sportIds.length === 0 ? <span className="staff-accessEmpty">All sports</span> : null}
                        {sportIds.map(sportId =>
                          this.renderRemovableChip(
                            this.getSportNameById(sportId),
                            () => this.removeIdFromPermission(permission, 'sportIds', sportId),
                            '#268049'
                          )
                        )}
                        <InlineMultiAdd
                          label="+ Add activities/Sports"
                          options={sportOptions}
                          onApply={sportIdsToAdd => this.addIdsToPermission(permission, 'sportIds', sportIdsToAdd)}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        {primaryPermission && primaryPermission.preset !== ROLE.SLT ? (
          <>
            <section className="staff-accessSection">
              <div className="staff-accessSection__head">
                <span>Forms</span>
              </div>
              <div className="staff-accessChips">
                {formIds.length === 0 ? <span className="staff-accessEmpty">No forms assigned.</span> : null}
                {formIds.map(formId => {
                  const form = this.state.forms.find(item => item.id === formId);
                  return this.renderRemovableChip(
                    form ? form.name : formId,
                    () => this.removeIdFromPermission(primaryPermission, 'formIds', formId),
                    '#5b6b7b'
                  );
                })}
                <InlineMultiAdd
                  label="+ Add form"
                  options={formOptions}
                  onApply={formIdsToAdd => this.addIdsToPermission(primaryPermission, 'formIds', formIdsToAdd)}
                />
              </div>
            </section>

            <section className="staff-accessSection">
              <div className="staff-accessSection__head">
                <span>Houses</span>
              </div>
              <div className="staff-accessChips">
                {houseIds.length === 0 ? <span className="staff-accessEmpty">No houses assigned.</span> : null}
                {houseIds.map(houseId => {
                  return this.renderRemovableChip(
                    this.getHouseNameById(houseId),
                    () => this.removeIdFromPermission(primaryPermission, 'houseIds', houseId),
                    this.getHouseColorById(houseId)
                  );
                })}
                <InlineMultiAdd
                  label="+ Add house"
                  options={houseOptions}
                  onApply={houseIdsToAdd => this.addIdsToPermission(primaryPermission, 'houseIds', houseIdsToAdd)}
                />
              </div>
            </section>
          </>
        ) : null}
      </div>
    );
  }
  getStaffSummaryTabs(): React.ReactNode {
    const { currentTabIndex, previewStaff } = this.state;

    if (!previewStaff) {
      return null;
    }

    switch (currentTabIndex) {
      case 0:
        return <StaffSummary staff={previewStaff} />;
      case 1:
        return this.renderStaffAccessEditor(previewStaff);
      default:
        console.error('Staffs.tsx \n Wrong tab index ' + currentTabIndex);
        return null;
    }
  }
  onEditRoleClick = (permission): void => {
    const { user } = this.props;
    if (this.isProtectedRoleForManager(permission)) {
      return;
    }
    const isCoachOrTeacher = permission.preset === ROLE.COACH || permission.preset === ROLE.TEACHER;

    this.setState({
      isLoading: true
    });

    if (isCoachOrTeacher) {
      const filter = {
        where: {
          id: {
            $in: permission.sportIds
          }
        },
        limit: 10,
        order: 'name ASC',
        skip: DEFAULT_SKIP
      };
      getSchoolSports(user, filter).then(sports => {
        this.setState({
          sports: sports,
          isLoading: false,
          editablePermissionId: permission.id,
          isAddRoleModalOpen: true
        });
      });
    } else {
      this.setState({
        isLoading: false,
        editablePermissionId: permission.id,
        isAddRoleModalOpen: true
      });
    }
  };

  render() {
    const {
      isLoading,
      isMessageModalOpen,
      isAddRoleModalOpen,
      isUserTagModalOpen,
      isUserTagDeleteModalOpen,
      isListActionsModalOpen
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isMessageModalOpen ||
      this.state.isCreateStaffModalOpen ||
      isAddRoleModalOpen ||
      isUserTagModalOpen ||
      isUserTagDeleteModalOpen ||
      isListActionsModalOpen
        ? 'staffsPage schoolPeoplePage modal-open'
        : 'staffsPage schoolPeoplePage';

    return (
      <div className={classes}>
        {this.renderUserTagAssignModal()}
        {this.renderUserTagDeleteModal()}
        {this.renderAddRoleModal()}
        {this.renderGMWizzard()}
        {this.renderActionsModal()}
        {this.renderInlineRevokeRoleModal()}
        {this.state.isCreateStaffModalOpen && <CreateStaffModal user={this.props.user} onClose={created => {
          this.setState({isCreateStaffModalOpen: false});
          if (created) this.setItems();
        }} />}
        <PeopleListPageHeader title="Staff" total={this.state.total} actions={
          this.props.user.activeRole === ROLE.ADMIN && getCreatableStaffRoles(this.props.user.activeSchool).length > 0 &&
            <button type="button" className="people-btn people-btn--primary" onClick={() => this.setState({isCreateStaffModalOpen: true})}>+ Add staff member</button>
        } />
        <div className="people-pageWrap">
          {this.renderStaffTable()}
          {this.renderSummary()}
        </div>
      </div>
    );
  }
}
